import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const CURRENCY_SYMBOL = { GBP: '£', EUR: '€', USD: '$', PLN: 'zł' };
export const CURRENCIES = ['GBP', 'EUR', 'USD', 'PLN'];

export function formatMoney(minor, currency = 'GBP') {
  const sym = CURRENCY_SYMBOL[currency] || '';
  const val = Number(minor || 0) / 100;
  const s = val.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  return currency === 'PLN' ? `${s} ${sym}` : `${sym}${s}`;
}

export function formatMoneyShort(minor, currency = 'GBP') {
  const sym = CURRENCY_SYMBOL[currency] || '';
  const val = Number(minor || 0) / 100;
  const s = val.toLocaleString(undefined, { maximumFractionDigits: val % 1 === 0 ? 0 : 2 });
  return currency === 'PLN' ? `${s} ${sym}` : `${sym}${s}`;
}

export const SELLER_LABEL = { mr_relative: 'MR. RELATIVE', relative_gallery: 'THE RELATIVE GALLERY' };
export const SALE_TYPE_LABEL = { artwork: 'ARTWORK', card: 'CARD', other: 'OTHER' };

export function serverError(err, fallback = 'Something went wrong') {
  const d = err?.response?.data?.detail;
  if (typeof d === 'string') return d;
  // FastAPI 422 → detail is an array of validation errors. Surface a readable
  // message (e.g. "Buyer email: value is not a valid email address").
  if (Array.isArray(d) && d.length) {
    const first = d[0];
    const msg = (first?.msg || '').replace(/^value error,?\s*/i, '');
    const loc = Array.isArray(first?.loc) ? first.loc.filter((p) => p !== 'body') : [];
    const field = loc.length ? loc[loc.length - 1] : null;
    if (msg) {
      const label = field
        ? String(field).replace(/_/g, ' ').replace(/^\w/, (c) => c.toUpperCase())
        : null;
      return label ? `${label}: ${msg}` : msg;
    }
  }
  if (d?.detail) return d.detail;
  return err?.message || fallback;
}

export const salesApi = {
  dashboard: (params) => axios.get(`${API}/admin/sales/dashboard`, { params }).then((r) => r.data),
  list: (params) => axios.get(`${API}/admin/sales`, { params }).then((r) => r.data),
  get: (id) => axios.get(`${API}/admin/sales/${id}`).then((r) => r.data),
  create: (body) => axios.post(`${API}/admin/sales`, body).then((r) => r.data),
  cancel: (id) => axios.post(`${API}/admin/sales/${id}/cancel`).then((r) => r.data),
  artists: () => axios.get(`${API}/admin/sales/artists-picklist`).then((r) => r.data),
  items: (artist_id) => axios.get(`${API}/admin/sales/items-picklist`, { params: { artist_id } }).then((r) => r.data),
  clientLookup: (email) => axios.get(`${API}/admin/sales/client-lookup`, { params: { email } }).then((r) => r.data),
  clients: (search) => axios.get(`${API}/admin/sales/clients/list`, { params: { search } }).then((r) => r.data),
  client: (id) => axios.get(`${API}/admin/sales/clients/${id}`).then((r) => r.data),
  updateClient: (id, body) => axios.patch(`${API}/admin/sales/clients/${id}`, body).then((r) => r.data),
  cards: (status) => axios.get(`${API}/admin/sales/cards`, { params: { status } }).then((r) => r.data),
  createCard: (body) => axios.post(`${API}/admin/sales/cards`, body).then((r) => r.data),
  bulkCards: (body) => axios.post(`${API}/admin/sales/cards/bulk`, body).then((r) => r.data),
  updateCard: (id, body) => axios.patch(`${API}/admin/sales/cards/${id}`, body).then((r) => r.data),
  deleteCard: (id) => axios.delete(`${API}/admin/sales/cards/${id}`).then((r) => r.data),
  giftCard: (id, body) => axios.post(`${API}/admin/sales/cards/${id}/gift`, body || {}).then((r) => r.data),
  giftRecipient: (id, body) => axios.post(`${API}/admin/sales/cards/${id}/gift-recipient`, body).then((r) => r.data),
  releaseCard: (id) => axios.post(`${API}/admin/sales/cards/${id}/release`).then((r) => r.data),
  drawPools: () => axios.get(`${API}/admin/sales/draws/pools`).then((r) => r.data),
  draws: () => axios.get(`${API}/admin/sales/draws`).then((r) => r.data),
  createDraw: (body) => axios.post(`${API}/admin/sales/draws`, body).then((r) => r.data),
  draw: (id) => axios.get(`${API}/admin/sales/draws/${id}`).then((r) => r.data),
  runDraw: (id) => axios.post(`${API}/admin/sales/draws/${id}/run`).then((r) => r.data),
  drawTestEmail: (id, body) => axios.post(`${API}/admin/sales/draws/${id}/test-email`, body).then((r) => r.data),
  finishDraw: (id) => axios.post(`${API}/admin/sales/draws/${id}/finish`, { confirm: true }).then((r) => r.data),
  deleteDraw: (id) => axios.delete(`${API}/admin/sales/draws/${id}`).then((r) => r.data),
  stripeSettings: () => axios.get(`${API}/admin/sales/settings/stripe`).then((r) => r.data),
  saveStripe: (body) => axios.post(`${API}/admin/sales/settings/stripe`, body).then((r) => r.data),
  testStripe: () => axios.post(`${API}/admin/sales/settings/stripe/test`).then((r) => r.data),
  drawSettings: () => axios.get(`${API}/admin/sales/settings/draw`).then((r) => r.data),
  saveDraw: (draw_date) => axios.post(`${API}/admin/sales/settings/draw`, { draw_date }).then((r) => r.data),
  publicStatus: (code) => axios.get(`${API}/public/sales/${code}/status`).then((r) => r.data),
  cardsConfig: () => axios.get(`${API}/public/cards/config`).then((r) => r.data),
};
